import { styled, Table, TableCell, tableCellClasses } from "@mui/material"
import { COLORS } from "../../utils"

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.root}`]: {
    height: 50,
  },
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: COLORS.PRIMARY_COLOR,
    color: theme.palette.common.white,
  },
}))

/**
 * StyledTable 2025 design
 *
 * A wrapper around styled tables with:
 * - fixed layout (columns share width evenly unless constrained)
 * - rounded corners (8px)
 * - "separate" borderCollapse (to allow visible borderRadius)
 *
 * Use this for consistent table styling across the app.
 */
export const StyledTable = styled(Table)({
  tableLayout: "fixed",
  width: "100%",
  borderRadius: "8px",
  overflow: "hidden",
  borderCollapse: "separate", // needed for borderRadius to show
})

/**
 * StyledHeaderCell 2025 design
 *
 * Header cell variant for table column headers.
 *
 * Props:
 * - `active?: boolean` → highlights the column (blue background, white bottom border).
 *
 * Default: grey/blue background with medium weight text.
 */
export const StyledHeaderCell = styled(TableCell, {
  shouldForwardProp: prop => prop !== "active"
})(({ active }) => {
  const styles = {
    fontSize: "16px",
    lineHeight: "24px",
    padding: "16px",
    textAlign: "left",
    alignItems: "center",
    backgroundColor: COLORS.PRIMARY_BLUE_20,
    color: COLORS.BLACK_600,
    fontWeight: 500,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    border: "1px solid " + COLORS.PRIMARY_BLUE_20,
    borderBottom: "none",
  }

  // Active header: highlight with stronger background + bottom white separator
  if (active) {
    styles.backgroundColor = COLORS.BLUE_50
    styles.fontWeight = 500
    styles.border = "1px solid " + COLORS.BLUE_50
    styles.borderBottom = "1px solid " + COLORS.WHITE
  }
  return styles
})

/**
 * StyledBodyCell 2025 design
 *
 * Body cell variant for table rows.
 *
 * Props:
 * - `active?: boolean` → highlights the cell (blue background, primary text color).
 * - `leftEdge?: boolean` → adds a left border.
 * - `rightEdge?: boolean` → adds a right border.
 * - `lastBodyRow?: boolean` → removes the bottom border.
 * - `lastTableRow?: boolean` → rounds the bottom corners if on table edges.
 */
export const StyledBodyCell = styled(TableCell, {
  shouldForwardProp: prop =>
    prop !== "active" &&
    prop !== "leftEdge" &&
    prop !== "rightEdge" &&
    prop !== "lastBodyRow" &&
    prop !== "lastTableRow" &&
    prop !== "padding"
})(({ active, leftEdge, rightEdge, lastBodyRow, lastTableRow, padding }) => {
  const styles = {
    fontSize: 16,
    lineHeight: "24px",
    padding: padding ? padding : "16px",
    textAlign: "left",
    color: COLORS.BLACK800,
    backgroundColor: COLORS.WHITE,
    fontWeight: 400,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    borderBottom: "1px solid " + COLORS.PRIMARY_BORDER,
  }

  if (leftEdge) {
    styles.borderLeft = "1px solid " + COLORS.PRIMARY_BORDER
  }

  if (rightEdge) {
    styles.borderRight = "1px solid " + COLORS.PRIMARY_BORDER
    if (active) styles.borderRight = "1px solid " + COLORS.BLUE_50
  }

  if (lastBodyRow) {
    styles.borderBottom = "none" // prevent double border when totals row follows
  }

  if (lastTableRow) {
    if (leftEdge) styles.borderBottomLeftRadius = "8px"
    if (rightEdge) styles.borderBottomRightRadius = "8px"
  }

  // Active cell override
  if (active) {
    styles.backgroundColor = COLORS.BLUE_50
    styles.color = COLORS.PRIMARY_COLOR
    styles.fontWeight = 500
    styles.borderBottom = "1px solid " + COLORS.WHITE
  }

  return styles
})

/**
 * StyledFooterCell 2025 design
 *
 * Special cell variant for totals row.
 *
 * Props:
 * - `active?: boolean` → highlights totals (blue background, darker text).
 *
 * Default: grey background, medium-weight text.
 */
export const StyledFooterCell = styled(TableCell, {
  shouldForwardProp: prop => prop !== "active"
})(({ active }) => {
  const styles = {
    fontSize: "16px",
    lineHeight: "24px",
    padding: "16px",
    textAlign: "left",
    backgroundColor: COLORS.BACKGROUND_GREY,
    color: COLORS.BLACK800,
    fontWeight: 500,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    border: "1px solid " + COLORS.BACKGROUND_GREY,
    borderTop: "none",
  }

  if (active) {
    styles.backgroundColor = COLORS.BLUE_50
    styles.color = COLORS.DARK_BLACK
    styles.fontWeight = 500
    styles.border = "1px solid " + COLORS.BLUE_50
    styles.borderTop = "none"
  }

  return styles
})